/**
 * Event delegation for HTMX-swapped content.
 *
 * The pane's views are swapped in and out by HTMX, so a handler bound directly to
 * an element would break the moment its view is replaced. Instead we bind ONE
 * capture-phase listener on `document` per (event, data-attr) and match with
 * `closest()`, so a handler keeps working no matter how often its target is
 * re-rendered.
 */

export const Events = {
  /**
   * Register a delegated handler keyed by a data attribute.
   *
   * @param {string} eventType - DOM event type (click, input, change, …)
   * @param {string} dataAttr - data attribute name (without the `data-` prefix)
   * @param {(event: Event, element: HTMLElement) => void} handler
   *
   * @example
   * Events.on("click", "oa-send", (e, el) => { ... });
   */
  on(eventType, dataAttr, handler) {
    document.addEventListener(
      eventType,
      (e) => {
        // Not every event target is an Element — one dispatched on document
        // or window has no closest(), and this runs before the try below.
        if (!(e.target instanceof Element)) return;
        const element = e.target.closest(`[data-${dataAttr}]`);
        if (!element) return;
        try {
          handler(e, element);
        } catch (error) {
          console.error(`Error in ${eventType} handler for [data-${dataAttr}]:`, error);
        }
      },
      true, // capture phase — see module comment
    );
  },
};
